import React, { forwardRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import DatePicker from 'react-datepicker'
import RadioCheck from '../../../components/MessageManager/RadioCheck'
import dateicon from '../../../assets/icons/message-manager/date.svg'
import timeicon from '../../../assets/icons/message-manager/time.svg'
import commonFunctions from '../../../utils/commonFunctions'
import { SIGNAGE_MEDIA } from '../../../utils/constants'

const ScheduleDateTimePanel = ({ scheduler, setScheduler, startingDateValid, startingTimeValid, endingDateValid, endingTimeValid, recurringValid, validated, markUnsavedChanges }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  // CustomInput component for DatePicker
  const CustomInput = forwardRef(({ value, onClick, icon, placeholderText }, ref) => (
    <div className="position-relative">
      <input
        type="text"
        className="form-control"
        onClick={onClick}
        ref={ref}
        value={value || ''}
        readOnly
        placeholder={placeholderText}
      />
      <img src={icon} className="calendar-icon" />
    </div>
  ));

  // date selection selection
  const scheduleDate = (label, value, onChange, minDate, maxDate, valid, colClass = "col-6") => (
    <div className={`${colClass} position-relative schedule-datetime`}>
      <label className="font-weight-600 font-size-14 mb-2"><FormattedMessage id={label} />:</label>
      <DatePicker
        selected={value ? new Date(value) : null}
        onChange={onChange}
        dateFormat="M/d/yyyy"
        minDate={minDate}
        maxDate={maxDate}
        customInput={<CustomInput icon={dateicon} placeholderText={t("SELECT_DATE")} />}
        wrapperClassName={`${validated && (!valid ? 'is-valid' : 'is-invalid')}`}
      />
      {commonFunctions.InvalidFeedbackString(valid, label)}
    </div>
  );

  // Time selection selection
  const scheduleTime = (label, value, onChange, minTime, maxTime, valid, colClass = "col-6") => (
    <div className={`${colClass} position-relative schedule-datetime`}>
      <label className="font-weight-600 font-size-14 mb-2"><FormattedMessage id={label} />:</label>
      <DatePicker
        selected={value ? new Date(value) : null}
        onChange={onChange}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={1}
        timeCaption="Time"
        dateFormat="hh:mm aa"
        wrapperClassName={`${validated && (!valid ? 'is-valid' : 'is-invalid')}`}
        customInput={<CustomInput icon={timeicon} placeholderText={t("SELECT_TIME")} />}
        minTime={minTime ? new Date(minTime) : new Date(0, 0, 0, 0, 0, 0)}
        maxTime={maxTime ? new Date(maxTime) : new Date(0, 0, 0, 23, 59, 0)}
      />
      {commonFunctions.InvalidFeedbackString(valid, label)}
    </div>
  );

  // Repeat change handler
  const handleRepeatChange = (e) => {
    setScheduler(prev => ({
      ...prev,
      repeat: e.target.value
    }));
  };

  // Cycle day switch
  const handleDayToggle = (index) => {
    setScheduler(prev => {
      const arr = [...prev.recurringDays];
      arr[index] = !arr[index];
      return { ...prev, recurringDays: arr };
    });
  };

  // Render recurring days
  const renderRecurringDays = () => (
    <div className="col-6 pe-2 position-relative schedule-datetime">
      <div className="d-flex">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
          <button
            key={day}
            type="button"
            className={`btn btn-sm me-3 ${scheduler.recurringDays[index] ? 'btn-primary selected-day' : 'btn-outline-primary unselected-day'}`}
            onClick={() => {handleDayToggle(index); markUnsavedChanges && markUnsavedChanges();}}
          >
            {day}
          </button>
        ))}
      </div>
      {validated && commonFunctions.InvalidFeedbackString(recurringValid, '', "d-block")}
    </div>
  );

  return (
    <div className="mt-3">
      <label className="font-weight-600 font-size-14"><FormattedMessage id="SCHEDULE_SIGNAGE_HINT" /></label>
      <div className="row mb-3 mt-3">
        {scheduleDate(
          "STARTING_DATE",
          scheduler.startingDate,
          date => {setScheduler(prev => ({ ...prev, startingDate: new Date(date) })); markUnsavedChanges && markUnsavedChanges();},
          new Date(),
          scheduler.endingDate || undefined,
          startingDateValid,
          "col-6 pe-2"
        )}
        {scheduleDate(
          "ENDING_DATE",
          scheduler.endingDate,
          date => {setScheduler(prev => ({ ...prev, endingDate: new Date(date) })); markUnsavedChanges && markUnsavedChanges();},
          scheduler.startingDate || new Date(),
          undefined,
          endingDateValid,
          "col-6 ps-2"
        )}
      </div>
      {/* Repeat Radio button area */}
      <div className="row mb-3 mt-3 align-items-center">
        <label className="font-weight-600 font-size-14"><FormattedMessage id="REPEAT" />:</label>
        <div className='mt-2'>
          <RadioCheck
            options={{ [SIGNAGE_MEDIA.CREATE_SIGNAGE.DAILY]: "DAILY", [SIGNAGE_MEDIA.CREATE_SIGNAGE.RECURRING]: "CUSTOM_DAYS" }}
            name="repeat"
            selectedOption={scheduler.repeat}
            onChange={e => { handleRepeatChange(e); markUnsavedChanges && markUnsavedChanges(); }}
            inline
          />
        </div>
      </div>
      {scheduler.repeat === SIGNAGE_MEDIA.CREATE_SIGNAGE.RECURRING && (
        <div className="row mb-3">
          {renderRecurringDays()}
        </div>
      )}
      <div className="row mb-3 mt-3">
        {scheduleTime(
          "STARTING_TIME",
          scheduler.startingTime,
          time => {setScheduler(prev => ({ ...prev, startingTime: new Date(time) })); markUnsavedChanges && markUnsavedChanges();},
          null,
          scheduler.endingTime || undefined,
          startingTimeValid,
          "col-6 pe-2"
        )}
        {scheduleTime(
          "ENDING_TIME",
          scheduler.endingTime,
          time => {setScheduler(prev => ({ ...prev, endingTime: new Date(time) })); markUnsavedChanges && markUnsavedChanges();},
          scheduler.startingTime || null,
          null,
          endingTimeValid,
          "col-6 ps-2"
        )}
      </div>
    </div>
  );
};

export default ScheduleDateTimePanel;
